import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Download, RefreshCw, Loader2, Inbox, Search, ExternalLink,
  ChevronDown, ChevronUp, FileText, X, AlertTriangle, Mail, BarChart3, Table as TableIcon,
  LayoutDashboard, MessageSquare, ClipboardCheck,
} from "lucide-react";
import PilotMetrics from "./PilotMetrics";
import PilotSnapshot from "./PilotSnapshot";
import PilotFeedback from "./PilotFeedback";
import PostRepaymentAnalytics from "./PostRepaymentAnalytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  "New","Under Review","Need More Info",
  "Approved","Waitlisted","Denied","Funded","Repaid","Late","Not Repaid","Closed",
];
const RISK = ["", "Low", "Medium", "High"];

const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString(); } catch { return iso; } };
const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;

const StatusBadge = ({ status }) => {
  const map = {
    New: "bg-white/8 text-white/80 border-white/10",
    "Under Review": "bg-amber-500/10 text-amber-300 border-amber-500/30",
    "Need More Info": "bg-blue-500/10 text-blue-300 border-blue-500/30",
    Approved: "bg-[#00FF88]/10 text-[#00FF88] border-[#00FF88]/30",
    Waitlisted: "bg-[#14F195]/10 text-[#14F195] border-[#14F195]/30",
    Denied: "bg-red-500/10 text-red-300 border-red-500/30",
    Funded: "bg-violet-500/10 text-violet-300 border-violet-500/30",
    Repaid: "bg-[#00FF88]/15 text-[#00FF88] border-[#00FF88]/40",
    Late: "bg-orange-500/10 text-orange-300 border-orange-500/30",
    "Not Repaid": "bg-red-500/15 text-red-200 border-red-500/40",
    Closed: "bg-white/5 text-white/45 border-white/8",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold border ${map[status] || map.New}`}>
      {status}
    </span>
  );
};

const Stat = ({ label, value, sub }) => (
  <div className="glass rounded-2xl p-4">
    <div className="text-[10px] font-mono-c uppercase tracking-widest text-white/40">{label}</div>
    <div className="font-display font-black text-2xl mt-1">{value}</div>
    {sub && <div className="text-[11px] font-mono-c text-white/40 mt-0.5">{sub}</div>}
  </div>
);

/** Inline file preview with token-authed blob fetch */
const FileViewer = ({ fileId, filename, token, contentType, slot, size }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const load = async () => {
    if (blobUrl || loading) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pilot/file/${fileId}`, {
        headers: { "X-Admin-Token": token },
        responseType: "blob",
      });
      setBlobUrl(window.URL.createObjectURL(res.data));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => () => { if (blobUrl) window.URL.revokeObjectURL(blobUrl); }, [blobUrl]);

  return (
    <div className="rounded-xl border border-white/8 bg-[#0A0A0A] p-3">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="text-[10px] font-mono-c uppercase tracking-widest text-[#00FF88]">{slot.replace(/_/g, " ")}</div>
          <div className="text-xs text-white/70 truncate font-mono-c">{filename}</div>
          {size && <div className="text-[10px] text-white/35 font-mono-c">{Math.round(size / 1024)} KB</div>}
        </div>
        {!blobUrl && (
          <button
            onClick={load}
            disabled={loading || error}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-[#00FF88] hover:text-white border border-[#00FF88]/30 rounded-full px-3 py-1.5 disabled:opacity-50"
            data-testid={`pilot-file-view-${slot}`}
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
            {error ? "Failed" : loading ? "Loading" : "View"}
          </button>
        )}
        {blobUrl && (
          <a href={blobUrl} download={filename} className="inline-flex items-center gap-1.5 text-xs font-bold text-[#00FF88] hover:text-white border border-[#00FF88]/30 rounded-full px-3 py-1.5">
            <Download className="w-3 h-3" /> Download
          </a>
        )}
      </div>
      {blobUrl && (
        contentType?.startsWith("image/") ? (
          <img src={blobUrl} alt={filename} className="w-full max-h-[420px] object-contain rounded-lg bg-black" />
        ) : (
          <a href={blobUrl} target="_blank" rel="noreferrer" className="block text-xs text-[#00FF88] underline truncate">
            Open file ↗
          </a>
        )
      )}
    </div>
  );
};

const ApplicationRow = ({ app, token, testMode, onChange, onPreview }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(null);

  const sendEmail = async (template, confirmText) => {
    const finalConfirm = testMode
      ? `🧪 TEST MODE — Preview the ${template.replace(/_/g, " ")} email in a modal? No real email will be sent.`
      : confirmText;
    if (!window.confirm(finalConfirm)) return;
    setSending(template);
    try {
      const { data } = await axios.post(
        `${API}/pilot/${app.id}/email`,
        { template, test_mode: testMode },
        { headers: { "X-Admin-Token": token } }
      );
      if (data?.test_mode) {
        onPreview({ subject: data.subject, html: data.html, to: data.to, template });
      } else {
        toast.success(`${template.replace(/_/g, " ")} email sent to ${app.email}`);
        onChange();
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Email failed";
      if (status === 502) {
        toast.warning(`Email queued but delivery failed: ${detail}`);
      } else {
        toast.error(detail);
      }
    } finally {
      setSending(null);
    }
  };

  const markRepaid = async () => {
    if (!window.confirm(`Mark ${app.full_name || app.email} as Repaid? This sets repayment date to today and updates the repayment rate.`)) return;
    setSaving(true);
    try {
      await axios.post(`${API}/pilot/${app.id}/mark-repaid`, {}, { headers: { "X-Admin-Token": token } });
      toast.success("Marked Repaid.");
      setDraft({});
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not mark repaid");
    } finally {
      setSaving(false);
    }
  };

  const offerAlternative = async () => {
    const amt = Number(app.amount_requested) === 50 ? 25 : 25;
    if (!window.confirm(`Send an Alternative Float Offer for $${amt} to ${app.email}? This emails them a magic accept link.`)) return;
    setSending("alternative_offer");
    try {
      const { data } = await axios.post(
        `${API}/pilot/${app.id}/offer-alternative`,
        { amount: amt },
        { headers: { "X-Admin-Token": token } }
      );
      toast.success(`Alternative $${amt} Float offer sent to ${app.email}.`);
      // Show accept link in toast for quick copy
      if (data?.accept_url) {
        toast(`Accept link: ${data.accept_url}`, { duration: 8000 });
      }
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Offer failed");
    } finally {
      setSending(null);
    }
  };

  const markWaitlisted = async () => {
    if (!window.confirm(`Move ${app.full_name || app.email} to the Waitlist? This sends them the "Pilot is full" email with their unique referral link.`)) return;
    setSending("pilot_waitlisted");
    try {
      const { data } = await axios.post(
        `${API}/pilot/${app.id}/waitlist`,
        {},
        { headers: { "X-Admin-Token": token } }
      );
      toast.success(`${app.email} moved to Waitlisted.`);
      if (data?.referral_url) {
        toast(`Their referral URL: ${data.referral_url}`, { duration: 8000 });
      }
      onChange();
    } catch (err) {
      // 502 from Resend quota — DB-side update still succeeded; refresh anyway
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "";
      if (status === 502) {
        toast.warning(`Moved to Waitlisted, but email delivery failed: ${detail}. Refreshing row...`);
        onChange();
      } else {
        toast.error(detail || "Could not waitlist");
      }
    } finally {
      setSending(null);
    }
  };

  const copyReferralUrl = async () => {
    if (!app.referral_code) return;
    const url = `${window.location.origin}/?ref=${app.referral_code}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Referral link copied.");
    } catch {
      toast.error("Copy failed.");
    }
  };

  const f = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    if (Object.keys(draft).length === 0) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/pilot/${app.id}`, draft, { headers: { "X-Admin-Token": token } });
      toast.success("Saved");
      setDraft({});
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sig = app.risk_signals || {};

  return (
    <div className="rounded-2xl border border-white/8 bg-white/[0.02] overflow-hidden" data-testid={`pilot-row-${app.id}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left hover:bg-white/[0.02]"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-display font-extrabold tracking-tight text-base">
              {app.full_name || app.email}
            </span>
            <StatusBadge status={app.status} />
            {app.files && Object.values(app.files).filter(Boolean).length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border border-white/15 text-white/70" data-testid={`pilot-upload-count-${app.id}`}>
                📎 {Object.values(app.files).filter(Boolean).length} uploads
              </span>
            )}
            {app.founder_member_number && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono-c font-bold border border-[#00FF88]/40 bg-[#00FF88]/[0.06] text-[#00FF88]" data-testid={`pilot-member-num-${app.id}`}>
                Member #{app.founder_member_number}
              </span>
            )}
            {app.risk_label && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border border-white/15 text-white/70">
                <AlertTriangle className="w-3 h-3" /> {app.risk_label}
              </span>
            )}
          </div>
          <div className="text-xs text-white/45 font-mono-c truncate mt-1">
            {app.application_id} · {app.email} · ${app.amount_requested} · {app.city}, {app.state}
          </div>
        </div>
        <div className="text-[11px] font-mono-c text-white/40 shrink-0">{fmtDate(app.created_at)}</div>
        {open ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
      </button>

      {open && (
        <div className="border-t border-white/5 p-5 space-y-5">
          {/* Identity + financial summary */}
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-[10px] font-mono-c uppercase tracking-widest text-white/40 mb-1">Identity</div>
              <div className="text-white">{app.full_name}</div>
              <div className="text-white/60">{app.email} · {app.phone}</div>
              <div className="text-white/60">{app.city}, {app.state} {app.zip_code}</div>
              <div className="text-white/60">DOB {app.date_of_birth} · {app.age} yrs</div>
            </div>
            <div>
              <div className="text-[10px] font-mono-c uppercase tracking-widest text-white/40 mb-1">Ask</div>
              <div className="text-white text-lg font-extrabold">${app.amount_requested}</div>
              <div className="text-white/60">Use: {(app.use_cases || []).join(", ") || "—"}</div>
              <div className="text-white/55 italic mt-1">&ldquo;{app.use_explanation}&rdquo;</div>
              <div className="text-white/55 mt-1 text-xs">Pay via {app.payment_method} → {app.payment_handle}</div>
            </div>
          </div>

          {/* Gig context */}
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-[10px] font-mono-c uppercase tracking-widest text-white/40 mb-1">Gig context</div>
              <div className="text-white/75">{app.active_gig_status} · {app.gig_experience} · {app.days_per_week} days/wk</div>
              <div className="text-white/55">Weekly: {app.weekly_earnings_range} · Daily: {app.daily_earnings_range}</div>
              <div className="text-white/55">Next payout: {app.next_payout}</div>
              <div className="text-white/55">Platforms: {(app.platforms || []).join(", ")} · primary: {app.primary_platform}</div>
            </div>
            <div>
              <div className="text-[10px] font-mono-c uppercase tracking-widest text-white/40 mb-1">Validation signals</div>
              <div className="flex flex-wrap gap-1.5">
                {(sig.low || []).map((s) => <span key={s} className="px-2 py-0.5 rounded-full text-[10px] bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/30">{s}</span>)}
                {(sig.medium || []).map((s) => <span key={s} className="px-2 py-0.5 rounded-full text-[10px] bg-amber-500/10 text-amber-300 border border-amber-500/30">{s}</span>)}
                {(sig.high || []).map((s) => <span key={s} className="px-2 py-0.5 rounded-full text-[10px] bg-red-500/10 text-red-300 border border-red-500/30">{s}</span>)}
              </div>
              <div className="text-white/55 mt-2 text-xs">
                Names match — gig: <strong className="text-white/85">{app.gig_name_match}</strong> · payment: <strong className="text-white/85">{app.payment_name_match}</strong>
              </div>
              {app.name_mismatch_explanation && (
                <div className="text-white/55 text-xs italic mt-1">&ldquo;{app.name_mismatch_explanation}&rdquo;</div>
              )}
            </div>
          </div>

          {/* Uploaded files */}
          <div>
            <div className="text-[10px] font-mono-c uppercase tracking-widest text-white/40 mb-2">Uploads</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {["payment_screenshot","current_earnings_screenshot","previous_earnings_screenshot","additional_earnings_screenshot"].map((slot) => {
                const file = app.files?.[slot];
                if (!file) {
                  return slot === "additional_earnings_screenshot" ? null : (
                    <div key={slot} className="rounded-xl border border-white/8 bg-[#0A0A0A] p-3 text-xs text-white/40">
                      {slot.replace(/_/g, " ")}: missing
                    </div>
                  );
                }
                return (
                  <FileViewer
                    key={slot}
                    slot={slot}
                    fileId={file.id}
                    filename={file.filename}
                    contentType={file.content_type}
                    size={file.size}
                    token={token}
                  />
                );
              })}
            </div>
          </div>

          {/* Follow-up + Consents */}
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-[10px] font-mono-c uppercase tracking-widest text-white/40 mb-1">Follow-up opt-ins</div>
              <ul className="space-y-0.5 text-white/70">
                <li>Post-pilot survey: <strong>{app.followup_survey}</strong></li>
                <li>Founder interview: <strong>{app.founder_interview}</strong></li>
                <li>Testimonial: <strong>{app.testimonial_willing}</strong></li>
                <li>Beta tester: <strong>{app.beta_testing}</strong></li>
                <li>Source: <strong>{app.acquisition_source}</strong>{app.referral_handle ? ` (${app.referral_handle})` : ""}</li>
              </ul>
            </div>
            <div>
              <div className="text-[10px] font-mono-c uppercase tracking-widest text-white/40 mb-1">Agreements (all required)</div>
              <ul className="space-y-0.5 text-white/55 text-xs">
                <li>{app.consent_pilot_program ? "✓" : "—"} Founder-led pilot, no guarantee</li>
                <li>{(app.consent_repay_terms ?? app.consent_repay_6_days) ? "✓" : "—"} Repay within 3 days ($25) or 5 days ($50)</li>
                <li>{app.consent_no_interest ? "✓" : "—"} No interest / no late fees</li>
                <li>{app.consent_manual_repayment ? "✓" : "—"} Manual repayment</li>
                <li>{app.consent_accurate_info ? "✓" : "—"} Info accurate / belongs to me</li>
                <li>{app.consent_no_guarantee ? "✓" : "—"} No future-product guarantee</li>
                <li>{app.consent_contact ? "✓" : "—"} Contact consent</li>
                <li>{app.consent_honest_participation ? "✓" : "—"} Honest participation</li>
              </ul>
            </div>
          </div>

          {/* Admin controls */}
          <div className="rounded-2xl border border-white/8 bg-[#0A0A0A] p-4 space-y-3">
            <div className="text-[10px] font-mono-c uppercase tracking-widest text-[#00FF88]">// Admin controls</div>

            <div className="grid sm:grid-cols-3 gap-3">
              <label className="block">
                <span className="text-[10px] font-mono-c uppercase tracking-widest text-white/40">Status</span>
                <select
                  value={draft.status ?? app.status ?? "New"}
                  onChange={(e) => f("status", e.target.value)}
                  className="mt-1 w-full h-10 px-3 bg-[#050505] border border-white/10 rounded-xl text-white text-sm focus:border-[#00FF88]/50 focus:outline-none"
                  data-testid={`pilot-status-${app.id}`}
                >
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] font-mono-c uppercase tracking-widest text-white/40">Risk label</span>
                <select
                  value={draft.risk_label ?? app.risk_label ?? ""}
                  onChange={(e) => f("risk_label", e.target.value)}
                  className="mt-1 w-full h-10 px-3 bg-[#050505] border border-white/10 rounded-xl text-white text-sm focus:border-[#00FF88]/50 focus:outline-none"
                >
                  {RISK.map((r) => <option key={r} value={r}>{r || "— none —"}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] font-mono-c uppercase tracking-widest text-white/40">Amount approved ($)</span>
                <input
                  type="number" min="0" step="1"
                  value={draft.amount_approved ?? app.amount_approved ?? ""}
                  onChange={(e) => f("amount_approved", e.target.value === "" ? null : Number(e.target.value))}
                  className="mt-1 w-full h-10 px-3 bg-[#050505] border border-white/10 rounded-xl text-white text-sm focus:border-[#00FF88]/50 focus:outline-none"
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-mono-c uppercase tracking-widest text-white/40">Amount funded ($)</span>
                <input
                  type="number" min="0" step="1"
                  value={draft.amount_funded ?? app.amount_funded ?? ""}
                  onChange={(e) => f("amount_funded", e.target.value === "" ? null : Number(e.target.value))}
                  className="mt-1 w-full h-10 px-3 bg-[#050505] border border-white/10 rounded-xl text-white text-sm focus:border-[#00FF88]/50 focus:outline-none"
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-mono-c uppercase tracking-widest text-white/40">Funding date</span>
                <input
                  type="date"
                  value={draft.funding_date ?? app.funding_date ?? ""}
                  onChange={(e) => f("funding_date", e.target.value)}
                  className="mt-1 w-full h-10 px-3 bg-[#050505] border border-white/10 rounded-xl text-white text-sm focus:border-[#00FF88]/50 focus:outline-none"
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-mono-c uppercase tracking-widest text-white/40">Repayment due</span>
                <input
                  type="date"
                  value={draft.repayment_due_date ?? app.repayment_due_date ?? app.preferred_repayment_date ?? ""}
                  onChange={(e) => f("repayment_due_date", e.target.value)}
                  className="mt-1 w-full h-10 px-3 bg-[#050505] border border-white/10 rounded-xl text-white text-sm focus:border-[#00FF88]/50 focus:outline-none"
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-mono-c uppercase tracking-widest text-white/40">Repayment amount due ($)</span>
                <input
                  type="number" min="0" step="1"
                  value={draft.repayment_amount_due ?? app.repayment_amount_due ?? ""}
                  onChange={(e) => f("repayment_amount_due", e.target.value === "" ? null : Number(e.target.value))}
                  className="mt-1 w-full h-10 px-3 bg-[#050505] border border-white/10 rounded-xl text-white text-sm focus:border-[#00FF88]/50 focus:outline-none"
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-mono-c uppercase tracking-widest text-white/40">Amount received ($)</span>
                <input
                  type="number" min="0" step="1"
                  value={draft.repayment_amount_received ?? app.repayment_amount_received ?? ""}
                  onChange={(e) => f("repayment_amount_received", e.target.value === "" ? null : Number(e.target.value))}
                  className="mt-1 w-full h-10 px-3 bg-[#050505] border border-white/10 rounded-xl text-white text-sm focus:border-[#00FF88]/50 focus:outline-none"
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-mono-c uppercase tracking-widest text-white/40">Received on</span>
                <input
                  type="date"
                  value={draft.repayment_received_date ?? app.repayment_received_date ?? ""}
                  onChange={(e) => f("repayment_received_date", e.target.value)}
                  className="mt-1 w-full h-10 px-3 bg-[#050505] border border-white/10 rounded-xl text-white text-sm focus:border-[#00FF88]/50 focus:outline-none"
                />
              </label>
            </div>

            {[["admin_notes","Private admin notes"],["applicant_quality_notes","Applicant quality notes"],["repayment_notes","Repayment notes"],["testimonial_notes","Testimonial notes"]].map(([k, label]) => (
              <label key={k} className="block">
                <span className="text-[10px] font-mono-c uppercase tracking-widest text-white/40">{label}</span>
                <textarea
                  rows={2}
                  value={draft[k] ?? app[k] ?? ""}
                  onChange={(e) => f(k, e.target.value)}
                  className="mt-1 w-full px-3 py-2 bg-[#050505] border border-white/10 rounded-xl text-white text-sm focus:border-[#00FF88]/50 focus:outline-none resize-y"
                />
              </label>
            ))}

            <div className="flex items-center justify-between pt-2 flex-wrap gap-2">
              <a href={`mailto:${app.email}`} className="text-xs text-[#00FF88] hover:text-white inline-flex items-center gap-1.5">
                <ExternalLink className="w-3.5 h-3.5" /> Email applicant
              </a>
              <div className="flex items-center gap-2">
                {app.status !== "Repaid" && app.funding_date && (
                  <button
                    onClick={markRepaid}
                    disabled={saving}
                    className="inline-flex items-center gap-2 border border-[#00FF88]/40 text-[#00FF88] hover:bg-[#00FF88]/15 font-bold text-sm px-4 py-2 rounded-full disabled:opacity-50"
                    data-testid={`pilot-mark-repaid-${app.id}`}
                    title="Mark as Repaid · sets repayment date to today and updates rate"
                  >
                    Mark repaid
                  </button>
                )}
                <button
                  onClick={save}
                  disabled={saving || Object.keys(draft).length === 0}
                  className="inline-flex items-center gap-2 bg-[#00FF88] text-black font-bold text-sm px-5 py-2 rounded-full btn-glow disabled:opacity-50"
                  data-testid={`pilot-save-${app.id}`}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
                </button>
              </div>
            </div>
          </div>

          {/* Founder Pilot Capital Protection — Alternative Float Offer */}
          {Number(app.amount_requested) === 50 && !["Funded","Repaid","Denied","Closed"].includes(app.status) && (
            <div className={`rounded-2xl border p-4 ${app.alternative_float_accepted ? "border-[#00FF88]/40 bg-[#00FF88]/[0.05]" : app.alternative_float_offered ? "border-amber-500/30 bg-amber-500/[0.04]" : "border-white/10 bg-white/[0.02]"}`}>
              <div className="text-[10px] font-mono-c uppercase tracking-widest text-[#00FF88] mb-1">// Capital protection · alternative offer</div>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="text-xs text-white/65 flex-1 min-w-[220px]">
                  {app.alternative_float_accepted ? (
                    <>Applicant <strong className="text-[#00FF88]">accepted</strong> the alternative <strong className="text-white">${app.alternative_float_amount}</strong> Float on {fmtDate(app.alternative_offer_accepted_at)}.</>
                  ) : app.alternative_float_offered ? (
                    <>Alternative <strong className="text-white">${app.alternative_float_amount}</strong> Float offered on {fmtDate(app.alternative_offer_sent_at)}. Waiting on the applicant to click the accept link.</>
                  ) : (
                    <>Applicant requested <strong className="text-white">$50</strong>. To preserve pilot capital, offer them a smaller <strong className="text-white">$25</strong> Float instead of a denial — they can accept with one click via email.</>
                  )}
                </div>
                {!app.alternative_float_offered && (
                  <button
                    onClick={offerAlternative}
                    disabled={sending !== null}
                    className="inline-flex items-center gap-2 bg-[#00FF88] text-black font-bold text-xs px-4 py-2 rounded-full btn-glow disabled:opacity-50 whitespace-nowrap"
                    data-testid={`pilot-offer-alt-${app.id}`}
                  >
                    {sending === "alternative_offer" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                    Offer $25 instead
                  </button>
                )}
                {app.alternative_float_offered && !app.alternative_float_accepted && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold border border-amber-500/40 text-amber-300 whitespace-nowrap">
                    Awaiting accept
                  </span>
                )}
                {app.alternative_float_accepted && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold border border-[#00FF88]/40 text-[#00FF88] whitespace-nowrap">
                    Accepted ✓
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Referral + Waitlist panel */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
            <div className="text-[10px] font-mono-c uppercase tracking-widest text-[#00FF88] mb-2">// Referral · Waitlist</div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[200px]">
                <div className="text-[10px] font-mono-c uppercase tracking-widest text-white/40">Referral code</div>
                <div className="font-mono-c text-base text-white font-bold tracking-widest mt-0.5" data-testid={`pilot-ref-code-${app.id}`}>
                  {app.referral_code || "—"}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-mono-c uppercase tracking-widest text-white/40">Referrals</div>
                <div className="text-white font-bold text-base mt-0.5">
                  {app.referral_count || 0}
                  <span className="ml-2 text-xs text-[#00FF88] font-mono-c">{app.referral_tier && app.referral_tier !== "—" ? `· ${app.referral_tier}` : ""}</span>
                </div>
              </div>
              <div>
                <div className="text-[10px] font-mono-c uppercase tracking-widest text-white/40">Referred by</div>
                <div className="font-mono-c text-sm text-white/70 mt-0.5">{app.referred_by || "—"}</div>
              </div>
              <div className="ml-auto flex flex-wrap gap-2">
                {app.referral_code && (
                  <button
                    onClick={copyReferralUrl}
                    className="inline-flex items-center gap-1.5 text-xs font-bold border border-white/15 hover:border-[#00FF88]/40 text-white/80 hover:text-[#00FF88] rounded-full px-3 py-1.5"
                    data-testid={`pilot-copy-ref-${app.id}`}
                  >
                    Copy referral link
                  </button>
                )}
                {!["Funded", "Repaid", "Closed"].includes(app.status) && app.status !== "Waitlisted" && (
                  <button
                    onClick={markWaitlisted}
                    disabled={sending !== null}
                    className="inline-flex items-center gap-1.5 text-xs font-bold border border-[#00FF88]/40 text-[#00FF88] hover:bg-[#00FF88]/15 rounded-full px-4 py-1.5 disabled:opacity-50"
                    data-testid={`pilot-mark-waitlist-${app.id}`}
                    title='Sends the "Pilot is currently full" email and moves them to Waitlisted'
                  >
                    {sending === "pilot_waitlisted" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                    Mark as Waitlisted
                  </button>
                )}
                {app.status === "Waitlisted" && app.waitlisted_at && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold border border-[#00FF88]/40 text-[#00FF88]">
                    Waitlisted · {fmtDate(app.waitlisted_at)}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Templated email triggers */}
          <div className="rounded-2xl border border-[#00FF88]/20 bg-[#00FF88]/[0.03] p-4">
            <div className="text-[10px] font-mono-c uppercase tracking-widest text-[#00FF88] mb-3">// Send applicant email · Phase 2</div>
            <p className="text-xs text-white/55 mb-3">
              Each button sends a branded email to <strong className="text-white">{app.email}</strong>. Saves to the row&apos;s email log automatically. Confirm before sending — no undo.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {[
                {k:"approval",  l:"Approval",            c:"Send the APPROVAL email now?"},
                {k:"denial",    l:"Denial",              c:"Send the DENIAL email now?"},
                {k:"funded",    l:"Funds sent",          c:"Send the FUNDED email (money is on its way) now?"},
                {k:"reminder",  l:"Reminder (-2 days)",  c:"Send the 'due in 2 days' REMINDER email now?"},
                {k:"due",       l:"Due today",           c:"Send the 'due TODAY' email now?"},
                {k:"overdue",   l:"Overdue (+2 days)",   c:"Send the OVERDUE (+2 days) email now?"},
                {k:"thank_you", l:"Thank you + survey",  c:"Send the THANK-YOU + feedback survey link now?"},
              ].map((t) => (
                <button
                  key={t.k}
                  onClick={() => sendEmail(t.k, t.c)}
                  disabled={sending !== null}
                  className="inline-flex items-center justify-center gap-1.5 border border-[#00FF88]/30 text-[#00FF88] hover:bg-[#00FF88]/15 hover:border-[#00FF88]/60 text-xs font-bold px-3 py-2 rounded-xl transition-all disabled:opacity-50"
                  data-testid={`pilot-email-${t.k}-${app.id}`}
                >
                  {sending === t.k ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                  {t.l}
                </button>
              ))}
            </div>
            {Array.isArray(app.email_log) && app.email_log.length > 0 && (
              <div className="mt-3 pt-3 border-t border-white/5 space-y-1">
                <div className="text-[10px] font-mono-c uppercase tracking-widest text-white/40">Sent</div>
                {app.email_log.slice(-6).reverse().map((e, i) => (
                  <div key={i} className="text-[11px] font-mono-c text-white/55">
                    {e.ok ? "✓" : "✗"} {e.template} · {fmtDate(e.at)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const PilotTab = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [reloadTick, setReloadTick] = useState(0);
  const [view, setView] = useState("snapshot"); // snapshot | applications | feedback | metrics
  const [testMode, setTestMode] = useState(() => {
    try { return localStorage.getItem("gf_email_test_mode") === "1"; } catch { return false; }
  });
  const [preview, setPreview] = useState(null);

  const toggleTestMode = () => {
    setTestMode((prev) => {
      const next = !prev;
      try { localStorage.setItem("gf_email_test_mode", next ? "1" : "0"); } catch { /* noop */ }
      toast(next ? "🧪 Test Mode ON — emails will preview, not send." : "🔥 Test Mode OFF — emails will send for real.");
      return next;
    });
  };

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const res = await axios.get(`${API}/pilot/list?limit=2000`, { headers: { "X-Admin-Token": token } });
        if (!cancelled) {
          setData(res.data);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          toast.error("Failed to load pilot applications");
          setLoading(false);
        }
      }
    };
    setLoading(true);
    run();
    return () => { cancelled = true; };
  }, [token, reloadTick]);

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/pilot/export.csv`, {
        headers: { "X-Admin-Token": token },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const dispo = res.headers["content-disposition"] || "";
      const m = dispo.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : "gigafloat-pilot.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Export failed"); }
  };

  const downloadXlsx = async (path = "export.xlsx", fallback = "gigafloat-pilot.xlsx", success = "Excel downloaded") => {
    try {
      const res = await axios.get(`${API}/pilot/${path}`, {
        headers: { "X-Admin-Token": token },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }));
      const a = document.createElement("a");
      a.href = url;
      const dispo = res.headers["content-disposition"] || "";
      const m = dispo.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : fallback;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(success);
    } catch { toast.error("Excel export failed"); }
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    let items = data.items || [];
    if (statusFilter !== "all") items = items.filter((i) => i.status === statusFilter);
    const term = q.trim().toLowerCase();
    if (term) {
      items = items.filter((i) =>
        (i.full_name || "").toLowerCase().includes(term) ||
        (i.email || "").toLowerCase().includes(term) ||
        (i.phone || "").includes(term) ||
        (i.application_id || "").toLowerCase().includes(term) ||
        (i.city || "").toLowerCase().includes(term) ||
        (i.state || "").toLowerCase().includes(term) ||
        (i.zip_code || "").includes(term)
      );
    }
    return items;
  }, [data, q, statusFilter]);

  if (loading && !data) {
    return <div className="py-20 text-center text-white/40"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  }
  if (!data) return null;

  const money = data.money || {};

  return (
    <div data-testid="pilot-tab">
      {/* View toggle: Snapshot · Applications · Feedback · Metrics */}
      <div className="inline-flex items-center gap-1 bg-white/[0.04] border border-white/8 rounded-full p-1 mb-5 overflow-x-auto">
        {[
          { id: "snapshot",     label: "Snapshot",     icon: LayoutDashboard },
          { id: "applications", label: "Applications", icon: TableIcon },
          { id: "feedback",     label: "Feedback",     icon: MessageSquare },
          { id: "post_repayment", label: "Post-Repayment", icon: ClipboardCheck },
          { id: "metrics",      label: "Metrics",      icon: BarChart3 },
        ].map((t) => {
          const I = t.icon;
          const active = view === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setView(t.id)}
              className={`inline-flex items-center gap-2 text-xs font-bold px-4 py-1.5 rounded-full transition-all whitespace-nowrap ${
                active ? "bg-[#00FF88] text-black" : "text-white/65 hover:text-white"
              }`}
              data-testid={`pilot-view-${t.id}`}
            >
              <I className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {view === "snapshot" && <PilotSnapshot token={token} onMarkedRepaid={reload} />}
      {view === "feedback" && <PilotFeedback token={token} />}
      {view === "post_repayment" && <PostRepaymentAnalytics token={token} />}
      {view === "metrics" && <PilotMetrics token={token} refreshKey={reloadTick} />}

      {view === "applications" && <>
      {/* Investor-style summary */}
      <div className="glass rounded-2xl p-6 mb-6 edge-glow-soft">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-mono-c text-[10px] uppercase tracking-[0.25em] text-[#00FF88]">// Float Pilot · live</div>
            <div className="font-display font-extrabold text-2xl mt-1">{data.total.toLocaleString()} applications</div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={reload}
              className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white border border-white/10 hover:border-white/30 rounded-full px-4 py-2"
              data-testid="pilot-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              onClick={toggleTestMode}
              className={`inline-flex items-center gap-2 text-xs font-bold border rounded-full px-4 py-2 transition-all whitespace-nowrap ${testMode ? "border-amber-400/60 bg-amber-400/10 text-amber-300" : "border-white/15 text-white/65 hover:text-white hover:border-white/30"}`}
              data-testid="pilot-test-mode-toggle"
              title="When ON, email-trigger buttons open a preview modal instead of calling Resend."
            >
              🧪 Test Mode: {testMode ? "ON" : "OFF"}
            </button>
            <button
              onClick={downloadCsv}
              className="inline-flex items-center gap-2 border border-[#00FF88]/40 text-[#00FF88] hover:bg-[#00FF88]/10 font-bold text-sm px-5 py-2 rounded-full"
              data-testid="pilot-export-csv"
            >
              <Download className="w-4 h-4" /> CSV
            </button>
            <button
              onClick={() => downloadXlsx("export.xlsx", "gigafloat-pilot.xlsx", "Pilot Excel downloaded")}
              className="inline-flex items-center gap-2 bg-[#00FF88] text-black font-bold text-sm px-5 py-2 rounded-full btn-glow"
              data-testid="pilot-export-xlsx"
            >
              <Download className="w-4 h-4" /> Excel
            </button>
            <button
              onClick={() => downloadXlsx("export.combined.xlsx", "gigafloat-combined.xlsx", "Combined data downloaded")}
              className="inline-flex items-center gap-2 border border-white/15 text-white/80 hover:border-white/40 font-bold text-sm px-5 py-2 rounded-full"
              data-testid="pilot-export-combined"
              title="Combined waitlist + pilot + feedback workbook"
            >
              <Download className="w-4 h-4" /> All data
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat label="Requested" value={fmtMoney(money.requested)} />
          <Stat label="Approved"  value={fmtMoney(money.approved)} />
          <Stat label="Funded"    value={fmtMoney(money.funded)} />
          <Stat label="Repaid"    value={fmtMoney(money.repaid)} />
          <Stat label="Outstanding" value={fmtMoney(money.outstanding)} />
        </div>

        <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-2 text-center">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(statusFilter === s ? "all" : s)}
              className={`rounded-xl border px-3 py-2 transition-all ${
                statusFilter === s
                  ? "border-[#00FF88]/50 bg-[#00FF88]/10 text-white"
                  : "border-white/8 bg-white/[0.02] text-white/65 hover:border-white/20"
              }`}
              data-testid={`pilot-filter-${s.toLowerCase().replace(/\s/g,"-")}`}
            >
              <div className="text-lg font-display font-extrabold">{(data.by_status?.[s] ?? 0).toLocaleString()}</div>
              <div className="text-[10px] font-mono-c uppercase tracking-widest text-white/50">{s}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Search + status header */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, phone, app ID, city, ZIP…"
            className="w-full h-11 pl-9 pr-3 bg-[#0A0A0A] border border-white/10 rounded-full text-white placeholder:text-white/30 focus:outline-none focus:border-[#00FF88]/50 focus:ring-2 focus:ring-[#00FF88]/20 text-sm"
            data-testid="pilot-search"
          />
        </div>
        {statusFilter !== "all" && (
          <button
            onClick={() => setStatusFilter("all")}
            className="inline-flex items-center gap-1 text-xs font-mono-c uppercase tracking-widest text-white/60 hover:text-white border border-white/10 rounded-full px-3 py-1.5"
          >
            <X className="w-3 h-3" /> Clear &ldquo;{statusFilter}&rdquo;
          </button>
        )}
        <div className="text-xs text-white/40 font-mono-c">{filtered.length} shown</div>
      </div>

      {filtered.length === 0 ? (
        <div className="py-20 text-center text-white/40 glass rounded-2xl">
          <Inbox className="w-6 h-6 inline mb-2" />
          <div>No pilot applications match.</div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="pilot-list">
          {filtered.map((app) => (
            <ApplicationRow key={app.id} app={app} token={token} testMode={testMode} onChange={reload} onPreview={setPreview} />
          ))}
        </div>
      )}
      </>}

      {/* TEST MODE preview modal — renders the email HTML in an iframe */}
      {preview && (
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
          data-testid="pilot-email-preview-modal"
          onClick={() => setPreview(null)}
        >
          <div
            className="bg-[#050505] border border-[#00FF88]/30 rounded-2xl max-w-[720px] w-full max-h-[90vh] flex flex-col overflow-hidden shadow-[0_0_60px_rgba(0,255,136,0.15)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-white/8">
              <div>
                <div className="font-mono-c text-[10px] uppercase tracking-[0.25em] text-[#00FF88]">// Test mode · {preview.template?.replace(/_/g, " ")}</div>
                <div className="font-display font-extrabold text-lg mt-1 text-white">{preview.subject}</div>
                <div className="text-xs text-white/45 font-mono-c mt-0.5">to: {preview.to} · (not actually sent)</div>
              </div>
              <button
                onClick={() => setPreview(null)}
                className="text-white/55 hover:text-white p-1.5 rounded-full hover:bg-white/10"
                data-testid="pilot-email-preview-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <iframe
              srcDoc={preview.html}
              title="Email preview"
              className="flex-1 w-full bg-white"
              data-testid="pilot-email-preview-iframe"
              sandbox="allow-same-origin"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PilotTab;
